import type { GetStaticPropsContext } from 'next';
import { useRouter } from 'next/router';

import { BLOCKED_QUERY_SEGMENTS } from '#/lib/blockedQuerySegments';
import {
  doesPathExist,
  fetchPagePaths,
} from '#/lib/sanity/fetchers/fetchPagePaths';
import { fetchPageProps } from '#/lib/sanity/fetchers/fetchPageProps';
import {
  draftPageProps,
  pageProps,
  type PageProps,
} from '#/lib/sanity/queries/page';
import { extractSlugFromRouter } from '#/lib/utils';
import { Page } from '#/components/Page';
import { aboutSchema } from '#/components/SeoSchema/aboutSchema';
import { RenderSEOSchema } from '#/components/SeoSchema/Render';

export const config = {
  maxDuration: 30,
};

export async function getStaticPaths() {
  const paths = await fetchPagePaths();

  return {
    paths,
    fallback: 'blocking',
  };
}

export async function getStaticProps({
  params,
  draftMode,
}: GetStaticPropsContext<{ slug: string[] }>) {
  const slug = params?.slug.join('/');

  BLOCKED_QUERY_SEGMENTS.forEach((segment) => {
    if (slug?.includes(segment)) {
      return {
        notFound: true,
      };
    }
  });

  const exists = await doesPathExist({ slug, draftMode });
  if (!exists) {
    return {
      notFound: true,
    };
  }

  const props = await fetchPageProps({
    pageQuery: draftMode ? draftPageProps : pageProps,
    slug,
    draftMode,
  });

  if (!props.page) {
    return {
      notFound: true,
    };
  }

  return { props };
}

export default function Home(props: PageProps) {
  const { query } = useRouter();
  const slug = extractSlugFromRouter(query);

  return (
    <>
      {slug === 'about' && <RenderSEOSchema schemaData={aboutSchema} />}
      <Page sections={props.page?.sections} />
    </>
  );
}
